/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useMemo } from "react";

import { cssFns } from "@superweb/css";
import { useLocale } from "@superweb/intl";
import { useTypo, useUiColors } from "@superweb/ui";

const parkColors = [
  { color: "#68778E", backgroundColor: "#C4CCD7" },
  { color: "#FF6549", backgroundColor: "#FFD4C7" },
  { color: "#FF742B", backgroundColor: "#FFDAB2" },
  { color: "#E68E21", backgroundColor: "#FAE3AA" },
  { color: "#63B11E", backgroundColor: "#D3EDA6" },
  { color: "#46BC48", backgroundColor: "#C5F0C6" },
  { color: "#33BC74", backgroundColor: "#B9F0DA" },
  { color: "#33A6BC", backgroundColor: "#B9EAF0" },
  { color: "#5289D0", backgroundColor: "#CBE1F5" },
  { color: "#7472E6", backgroundColor: "#DAD9FA" },
  { color: "#D06DF0", backgroundColor: "#F5D7FC" },
  { color: "#FF6583", backgroundColor: "#FFD4DF" },
];

export const ParkAvatar = ({
  id,
  name,
  size = "m",
}: {
  id: string;
  name: string;
  size?: "s" | "m" | "l" | "xl";
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const getInitials = useGetInitials();
  const parkColor = parkColors[
    Array.from(id).reduce((a, b) => a + b.charCodeAt(0), 0) % parkColors.length
  ] ?? { color: uiColors.everFront, backgroundColor: uiColors.controlMinor };

  const avatarSize = {
    s: {
      width: "32px",
      height: "32px",
      level: "body2" as const,
      radius: "6px",
    },
    m: {
      width: "40px",
      height: "40px",
      level: "body1" as const,
      radius: "10px",
    },
    l: {
      width: "56px",
      height: "56px",
      level: "title3" as const,
      radius: "16px",
    },
    xl: {
      width: "64px",
      height: "64px",
      level: "title3" as const,
      radius: "16px",
    },
  }[size];

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: parkColor.color,
        backgroundColor: parkColor.backgroundColor,
        flexShrink: "0",
        ...avatarSize,
        ...typo({
          level: avatarSize.level,
          weight: "medium",
          density: "normal",
        }),
        ...cssFns.border({ radius: avatarSize.radius }),
      }}
    >
      {getInitials(name)}
    </div>
  );
};

/**
 * @deprecated Use ParkAvatar instead
 */
export const ParkAvatarDeprecated = ({
  id,
  name,
  size = "m",
}: {
  id: string;
  name: string;
  size?: "m" | "l";
}) => {
  const parkColor =
    parkColors[
      Array.from(id).reduce((a, b) => a + b.charCodeAt(0), 0) %
        parkColors.length
    ];

  const initials = useMemo(() => getInitials(name), [name]);

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: parkColor!.color,
        backgroundColor: parkColor!.backgroundColor,
        ...(size === "l"
          ? {
              width: "40px",
              height: "40px",
              ...cssFns.border({ radius: "10px" }),
            }
          : {
              width: "28px",
              height: "28px",
              ...cssFns.border({ radius: "6px" }),
            }),
      }}
    >
      {initials}
    </div>
  );
};

export const useGetInitials = () => {
  const locale = useLocale();
  const segmenter = new Intl.Segmenter(locale);
  const wordsSegmenter = new Intl.Segmenter(locale, { granularity: "word" });

  const wordSymbols = (word: string) => {
    return [...segmenter.segment(word)].map(({ segment }) => segment);
  };

  return (name: string) => {
    const formatedName = name.replaceAll(/[^\p{L} 0-9]/giu, "");
    const relevantWords = [...wordsSegmenter.segment(formatedName)].flatMap(
      ({ isWordLike, segment }) => (isWordLike ? [segment] : []),
    );
    const firstWordSymbols = wordSymbols(relevantWords[0] ?? "");
    const secondWordSymbols = wordSymbols(relevantWords[1] ?? "");
    const initialSymbols = relevantWords[1]
      ? [
          firstWordSymbols[0]?.toLocaleUpperCase(),
          secondWordSymbols[0]?.toLocaleUpperCase(),
        ]
      : [firstWordSymbols[0]?.toLocaleUpperCase(), firstWordSymbols[1]];

    return initialSymbols.filter(Boolean).join("");
  };
};

/**
 * @deprecated Use useGetInitials instead
 */
export const getInitials = (name: string) => {
  const relevantWorlds = name
    .trim()
    .replaceAll(
      /[«»`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/\p{Extended_Pictographic}]/giu,
      "",
    )
    .split(" ")
    .filter((e) => e)
    .slice(0, 2);

  if (relevantWorlds.length === 1) {
    const word = relevantWorlds[0];
    return (
      (word?.slice(0, 1).toLocaleUpperCase() ?? "") + (word?.slice(1, 2) ?? "")
    );
  } else {
    return relevantWorlds.reduce((accumulator, word) => {
      return accumulator + word.charAt(0).toUpperCase();
    }, "");
  }
};
